import { FormEvent, useState, ChangeEvent, useEffect } from "react";
import { GoEyeClosed, GoEye } from "react-icons/go";
import { useAppDispatch } from "../../redux/hooks";
import { useNavigate } from "react-router-dom";
import { salesAdminResetPassword } from "../../redux/thunk";
import LoadingSpinner from "../../components/elements/LoadingSpinner";

interface Errors {
  email?: string;
  otp?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const SalesOfficerResetPassword = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<Errors>({});

  const [inputValue, setInputValue] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Get email from sessionStorage if available
    const resetEmail = sessionStorage.getItem("resetEmail");
    if (resetEmail) {
      setInputValue((prev) => ({ ...prev, email: resetEmail }));
    }
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(
      (prevShowConfirmPassword) => !prevShowConfirmPassword
    );
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value.trim() !== "") {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
    setInputValue((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors: Errors = {};

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inputValue.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Validate OTP
    if (inputValue.otp.trim() === "") {
      newErrors.otp = "OTP is required";
      isValid = false;
    } else if (!/^\d+$/.test(inputValue.otp)) {
      newErrors.otp = "OTP should contain only numbers";
      isValid = false;
    }

    // Validate password
    const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(inputValue.newPassword)) {
      newErrors.newPassword =
        "Password must be at least 8 characters and contain both letters and numbers";
      isValid = false;
    }

    // Validate confirm password
    if (inputValue.newPassword !== inputValue.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    if (validateForm()) {
      try {
        const resetData = {
          email: inputValue.email.toLowerCase(),
          otp: inputValue.otp,
          newPassword: inputValue.newPassword,
        };

        const result = await dispatch(salesAdminResetPassword(resetData));

        if (result.type === "salesAdminResetPassword/fulfilled") {
          // Clear the stored email
          sessionStorage.removeItem("resetEmail");
          navigate("/sales-admin-login", { replace: true });
        }
      } catch (error) {
        console.error("Password reset failed:", error);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[100vh] py-8">
      <div className="px-4 lg:px-14 w-full lg:w-[45%]">
        <div className="flex items-center justify-center">
          <img
            src="assets/logo.png"
            alt="logo"
            className="pb-15 self-center w-[6rem]"
          />
        </div>
        <div className="flex items-center justify-center flex-col">
          <h1 className="font-bold text-xl mb-3 mt-9">Reset Password</h1>
          <p className="text-gray-600 mb-6 text-center">
            Enter the OTP sent to your email and create a new password
          </p>
        </div>
        <form className="px-4 lg:px-14 mb-[5rem]" onSubmit={handleSubmit}>
          <div className="flex flex-col mb-4">
            <label className="text-sm font-medium pb-1">Email</label>
            <input
              name="email"
              value={inputValue.email}
              onChange={handleInputChange}
              type="text"
              placeholder="Enter your email address"
              className="bg-gray-100 text-sm outline-none p-2 lg:p-3 rounded-md border border-gray-200"
              readOnly={!!sessionStorage.getItem("resetEmail")}
            />
            {errors.email && (
              <p className="text-red-500 text-xs">{errors.email}</p>
            )}
          </div>

          <div className="flex flex-col mb-4">
            <label className="text-sm font-medium pb-1">OTP</label>
            <input
              name="otp"
              value={inputValue.otp}
              onChange={handleInputChange}
              type="text"
              placeholder="Enter the OTP sent to your email"
              className="bg-gray-100 text-sm outline-none p-2 lg:p-3 rounded-md border border-gray-200"
            />
            {errors.otp && <p className="text-red-500 text-xs">{errors.otp}</p>}
          </div>

          <div className="flex flex-col relative mb-4">
            <label className="text-sm font-medium pb-1">New Password</label>
            <input
              name="newPassword"
              value={inputValue.newPassword}
              onChange={handleInputChange}
              type={showPassword ? "text" : "password"}
              placeholder="Enter your new password"
              className="bg-gray-100 text-sm outline-none p-2 lg:p-3 rounded-md border border-gray-200"
            />
            <figure
              className="absolute right-4 top-[65%] transform -translate-y-1/2 cursor-pointer"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <GoEye /> : <GoEyeClosed />}
            </figure>
            {errors.newPassword && (
              <p className="text-red-500 text-xs">{errors.newPassword}</p>
            )}
          </div>

          <div className="flex flex-col relative mb-4">
            <label className="text-sm font-medium pb-1">Confirm Password</label>
            <input
              name="confirmPassword"
              value={inputValue.confirmPassword}
              onChange={handleInputChange}
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your new password"
              className="bg-gray-100 text-sm outline-none p-2 lg:p-3 rounded-md border border-gray-200"
            />
            <figure
              className="absolute right-4 top-[65%] transform -translate-y-1/2 cursor-pointer"
              onClick={toggleConfirmPasswordVisibility}
            >
              {showConfirmPassword ? <GoEye /> : <GoEyeClosed />}
            </figure>
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs">{errors.confirmPassword}</p>
            )}
          </div>

          <button
            className="bg-secondary text-white w-[100%] text-base font-semibold p-2 lg:p-3 rounded-md shadow-sm mt-8 lg:mt-10 mb-6 transition-all duration-500 hover:border-secondary hover:border-2 hover:text-secondary hover:bg-transparent"
            type="submit"
          >
            {isLoading ? <LoadingSpinner /> : "Reset Password"}
          </button>
          <div className="text-center">
            <a href="/" className="text-green-600 hover:underline">
              Back to Login
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesOfficerResetPassword;
